import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore'
import { db } from './firebase'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Catalog entry: shared job role (name + accountabilities). */
export interface RoleDefinition {
  id: string
  name: string
  accountabilities: string[]
  /** When true, every org-chart seat using this role is a contractor position. */
  isContractor: boolean
  createdAt: Timestamp
  updatedAt: Timestamp
}

/** Where a role definition is used across org chart versions. */
export interface RoleDefinitionUsage {
  definition: RoleDefinition
  seatCount: number
  charts: Array<{
    id: string
    name: string
    status: 'live' | 'draft'
    seatCount: number
  }>
}

export interface Team {
  id: string
  name: string
  color: string
  createdAt: Timestamp
}

export interface TeamMember {
  id: string
  primaryEmail: string
  name: {
    givenName: string
    familyName: string
    fullName: string
  }
  thumbnailPhotoUrl: string | null
  /** Firebase Storage path for synced Workspace avatar (optional). */
  avatarStoragePath?: string | null
  /** Directory photo etag — skip re-upload when unchanged. */
  photoEtag?: string | null
  phone: string | null
  jobTitle: string | null
  orgUnitPath: string | null
  reportsToEmail: string | null  // Manager's email from Google Directory
  location: {
    label: string | null
    coordinates: {
      lat: number
      lng: number
    } | null
    type: 'office' | 'remote' | 'hybrid' | null
    structured?: {
      formatted: string
      streetAddress: string
      locality: string
      region: string
      postalCode: string
      country: string
      countryCode: string
      coordinates?: { lat: number; lng: number }
    }
  } | null
  lastSyncedAt: Timestamp
  syncSource: 'google' | 'manual'
  /** When true, the person is omitted from Directory and Location views (inactive Workspace users, group inboxes, etc.). */
  hidden?: boolean
}

/**
 * Prefer Firebase Storage avatars. Drop Google Directory private/public
 * thumbnail URLs — they fail intermittently in the browser.
 */
export function normalizeThumbnailUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') return null
  const trimmed = url.trim()
  if (!trimmed) return null
  if (trimmed.includes('firebasestorage.googleapis.com')) return trimmed
  if (
    trimmed.includes('googleusercontent.com') ||
    trimmed.includes('google.com/s2/photos') ||
    trimmed.includes('lh3.google')
  ) {
    return null
  }
  return trimmed
}

/** A named org-chart version. Exactly one should have status `live`. */
export interface OrgChartVersion {
  id: string
  name: string
  status: 'live' | 'draft'
  createdAt: Timestamp
  updatedAt: Timestamp
  createdBy?: string | null
}

/**
 * Org-chart seat/card. Title and accountabilities are denormalized from
 * the linked RoleDefinition so downstream readers can use them directly.
 */
export interface Role {
  id: string
  /** Which named org chart this seat belongs to. */
  orgChartId: string
  /** Links to roleDefinitions/{id}. Required for new seats. */
  roleDefinitionId: string | null
  title: string
  teams: string[]              // Can belong to multiple teams
  teamColors: string[]         // Colors for each team
  reportsToRoleId: string | null
  /**
   * Peer row rank among colleagues (same reportsTo). Same order = same
   * horizontal row; higher order rows render above lower ones.
   */
  order: number
  accountabilities: string[]
  assignedMemberId: string | null
  /** When true, denormalized from the linked role definition (contractor positions). */
  isContractor: boolean
  createdAt: Timestamp
  updatedAt: Timestamp
}

export interface RoleWithMember extends Role {
  assignedMember: TeamMember | null
  directReportsCount: number
  inheritedTeams: string[]       // Teams inherited from parent roles
  inheritedTeamColors: string[]  // Colors for inherited teams
}

// ============================================================================
// ORG CHART SERVICE
// ============================================================================

export class OrgChartService {
  private static instance: OrgChartService

  static getInstance(): OrgChartService {
    if (!OrgChartService.instance) {
      OrgChartService.instance = new OrgChartService()
    }
    return OrgChartService.instance
  }

  // ==========================================================================
  // ORG CHART VERSIONS (live + named drafts)
  // ==========================================================================

  async getAllOrgCharts(): Promise<OrgChartVersion[]> {
    const snapshot = await getDocs(collection(db, 'orgCharts'))
    const charts = snapshot.docs.map(d => ({
      id: d.id,
      ...d.data()
    })) as OrgChartVersion[]
    return charts.sort((a, b) => {
      if (a.status !== b.status) return a.status === 'live' ? -1 : 1
      return a.name.localeCompare(b.name)
    })
  }

  async getLiveOrgChart(): Promise<OrgChartVersion | null> {
    const q = query(collection(db, 'orgCharts'), where('status', '==', 'live'))
    const snapshot = await getDocs(q)
    if (snapshot.empty) return null
    const d = snapshot.docs[0]
    return { id: d.id, ...d.data() } as OrgChartVersion
  }

  async getOrgChart(id: string): Promise<OrgChartVersion | null> {
    const snapshot = await getDoc(doc(db, 'orgCharts', id))
    if (!snapshot.exists()) return null
    return { id: snapshot.id, ...snapshot.data() } as OrgChartVersion
  }

  async createOrgChart(
    name: string,
    status: 'live' | 'draft' = 'draft',
    createdBy?: string | null
  ): Promise<string> {
    // Never create a second live chart
    if (status === 'live') {
      const existing = await this.getLiveOrgChart()
      if (existing) {
        throw new Error('A live org chart already exists. Promote a draft instead.')
      }
    }

    const ref = doc(collection(db, 'orgCharts'))
    await setDoc(ref, {
      name: name.trim(),
      status,
      createdBy: createdBy ?? null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    return ref.id
  }

  async renameOrgChart(id: string, name: string): Promise<void> {
    await updateDoc(doc(db, 'orgCharts', id), {
      name: name.trim(),
      updatedAt: serverTimestamp()
    })
  }

  /**
   * Ensure exactly one Live chart exists and every role has orgChartId.
   * Creates "Live Organisation" and backfills legacy seats when needed.
   * Also repairs accidental duplicate live charts (e.g. race on first load).
   */
  async ensureOrgChartsMigrated(): Promise<OrgChartVersion> {
    const allCharts = await this.getAllOrgCharts()
    let liveCharts = allCharts.filter(c => c.status === 'live')

    // Repair: more than one live — keep the chart with the most seats
    if (liveCharts.length > 1) {
      const roleCounts = await Promise.all(
        liveCharts.map(async chart => ({
          chart,
          count: (await this.getAllRoles(chart.id)).length
        }))
      )
      roleCounts.sort((a, b) => b.count - a.count)
      const keeper = roleCounts[0].chart

      for (const { chart, count } of roleCounts.slice(1)) {
        if (count === 0) {
          // Empty duplicate from a migration race — remove it
          await deleteDoc(doc(db, 'orgCharts', chart.id))
        } else {
          await updateDoc(doc(db, 'orgCharts', chart.id), {
            status: 'draft',
            name: chart.name.includes('(duplicate)')
              ? chart.name
              : `${chart.name} (duplicate)`,
            updatedAt: serverTimestamp()
          })
        }
      }

      liveCharts = [keeper]
    }

    let live = liveCharts[0] ?? null
    if (!live) {
      const liveId = await this.createOrgChart('Live Organisation', 'live')
      live = (await this.getOrgChart(liveId))!
    }

    // Backfill roles missing orgChartId onto the live chart
    const allRolesSnap = await getDocs(collection(db, 'roles'))
    const legacy = allRolesSnap.docs.filter(d => !d.data().orgChartId)
    await Promise.all(
      legacy.map(d =>
        updateDoc(d.ref, {
          orgChartId: live!.id,
          updatedAt: serverTimestamp()
        })
      )
    )

    return live
  }

  /**
   * Clone an existing chart's seats into a new named draft.
   * Remaps reportsToRoleId to the cloned seat ids.
   */
  async createDraftFromChart(
    sourceChartId: string,
    name: string,
    createdBy?: string | null
  ): Promise<string> {
    const draftId = await this.createOrgChart(name, 'draft', createdBy)
    const sourceRoles = await this.getAllRoles(sourceChartId)
    const idMap = new Map<string, string>()

    // First pass: create seats with remapped ids, parents set later
    for (const role of sourceRoles) {
      const newRef = doc(collection(db, 'roles'))
      idMap.set(role.id, newRef.id)
      await setDoc(newRef, {
        orgChartId: draftId,
        roleDefinitionId: role.roleDefinitionId,
        title: role.title,
        teams: role.teams || [],
        teamColors: role.teamColors || [],
        reportsToRoleId: null,
        order: role.order ?? 0,
        accountabilities: role.accountabilities || [],
        assignedMemberId: role.assignedMemberId,
        isContractor: !!role.isContractor,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
    }

    // Second pass: remap reporting lines
    await Promise.all(
      sourceRoles.map(async role => {
        const newId = idMap.get(role.id)
        if (!newId) return
        const newParentId = role.reportsToRoleId
          ? idMap.get(role.reportsToRoleId) ?? null
          : null
        if (newParentId) {
          await updateDoc(doc(db, 'roles', newId), {
            reportsToRoleId: newParentId,
            updatedAt: serverTimestamp()
          })
        }
      })
    )

    return draftId
  }

  /**
   * Promote a draft to live. Current live becomes a draft (keeps its name).
   */
  async promoteDraftToLive(draftId: string): Promise<void> {
    const draft = await this.getOrgChart(draftId)
    if (!draft) throw new Error('Chart not found')
    if (draft.status === 'live') return

    const allCharts = await this.getAllOrgCharts()
    const otherLive = allCharts.filter(c => c.status === 'live' && c.id !== draftId)
    await Promise.all(
      otherLive.map(live =>
        updateDoc(doc(db, 'orgCharts', live.id), {
          status: 'draft',
          updatedAt: serverTimestamp()
        })
      )
    )

    await updateDoc(doc(db, 'orgCharts', draftId), {
      status: 'live',
      updatedAt: serverTimestamp()
    })
  }

  /**
   * Delete a draft chart and all of its seats. Cannot delete the live chart.
   */
  async deleteDraftChart(chartId: string): Promise<void> {
    const chart = await this.getOrgChart(chartId)
    if (!chart) throw new Error('Chart not found')
    if (chart.status === 'live') {
      throw new Error('Cannot delete the live org chart. Promote another draft first.')
    }

    const roles = await this.getAllRoles(chartId)
    await Promise.all(roles.map(r => deleteDoc(doc(db, 'roles', r.id))))
    await deleteDoc(doc(db, 'orgCharts', chartId))
  }

  // ==========================================================================
  // ROLE DEFINITIONS CRUD (catalog of named roles)
  // ==========================================================================

  async getAllRoleDefinitions(): Promise<RoleDefinition[]> {
    try {
      const ref = collection(db, 'roleDefinitions')
      const snapshot = await getDocs(ref)
      const defs = snapshot.docs.map(d => {
        const data = d.data()
        return {
          id: d.id,
          ...data,
          isContractor: !!data.isContractor
        } as RoleDefinition
      })
      return defs.sort((a, b) => a.name.localeCompare(b.name))
    } catch (error) {
      console.error('Error fetching role definitions:', error)
      throw error
    }
  }

  async getRoleDefinition(id: string): Promise<RoleDefinition | null> {
    try {
      const snapshot = await getDoc(doc(db, 'roleDefinitions', id))
      if (!snapshot.exists()) return null
      const data = snapshot.data()
      return {
        id: snapshot.id,
        ...data,
        isContractor: !!data.isContractor
      } as RoleDefinition
    } catch (error) {
      console.error('Error fetching role definition:', error)
      throw error
    }
  }

  async createRoleDefinition(
    data: Omit<RoleDefinition, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const ref = doc(collection(db, 'roleDefinitions'))
      await setDoc(ref, {
        name: data.name.trim(),
        accountabilities: data.accountabilities || [],
        isContractor: !!data.isContractor,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      return ref.id
    } catch (error) {
      console.error('Error creating role definition:', error)
      throw error
    }
  }

  async updateRoleDefinition(
    id: string,
    updates: Partial<Pick<RoleDefinition, 'name' | 'accountabilities' | 'isContractor'>>
  ): Promise<void> {
    try {
      const payload: {
        updatedAt: ReturnType<typeof serverTimestamp>
        name?: string
        accountabilities?: string[]
        isContractor?: boolean
      } = { updatedAt: serverTimestamp() }
      if (updates.name !== undefined) payload.name = updates.name.trim()
      if (updates.accountabilities !== undefined) {
        payload.accountabilities = updates.accountabilities
      }
      if (updates.isContractor !== undefined) {
        payload.isContractor = !!updates.isContractor
      }
      await updateDoc(doc(db, 'roleDefinitions', id), payload)

      // Keep denormalized fields on org-chart seats in sync
      const seats = await this.getAllRoles()
      const linked = seats.filter(s => s.roleDefinitionId === id)
      const name = updates.name !== undefined ? updates.name.trim() : undefined
      const accountabilities = updates.accountabilities
      const isContractor = updates.isContractor
      await Promise.all(
        linked.map(seat =>
          this.updateRole(seat.id, {
            ...(name !== undefined ? { title: name } : {}),
            ...(accountabilities !== undefined ? { accountabilities } : {}),
            ...(isContractor !== undefined ? { isContractor: !!isContractor } : {})
          })
        )
      )
    } catch (error) {
      console.error('Error updating role definition:', error)
      throw error
    }
  }

  async deleteRoleDefinition(id: string): Promise<void> {
    try {
      const seats = await this.getAllRoles()
      const inUse = seats.some(s => s.roleDefinitionId === id)
      if (inUse) {
        throw new Error(
          'This role is assigned to one or more org chart cards. Reassign those cards first.'
        )
      }
      await deleteDoc(doc(db, 'roleDefinitions', id))
    } catch (error) {
      console.error('Error deleting role definition:', error)
      throw error
    }
  }

  /**
   * List every role definition with which org charts (and how many seats) use it.
   */
  async getRoleDefinitionUsage(): Promise<RoleDefinitionUsage[]> {
    const [defs, seats, charts] = await Promise.all([
      this.getAllRoleDefinitions(),
      this.getAllRoles(),
      this.getAllOrgCharts()
    ])
    const chartsById = new Map(charts.map(c => [c.id, c]))

    return defs.map(definition => {
      const linked = seats.filter(s => s.roleDefinitionId === definition.id)
      const byChart = new Map<string, number>()
      for (const seat of linked) {
        const chartId = seat.orgChartId || ''
        byChart.set(chartId, (byChart.get(chartId) || 0) + 1)
      }

      const chartUsages = [...byChart.entries()]
        .map(([id, seatCount]) => {
          const chart = chartsById.get(id)
          return {
            id: id || 'unknown',
            name: chart?.name || (id ? 'Unknown chart' : 'Unassigned chart'),
            status: (chart?.status ?? 'draft') as 'live' | 'draft',
            seatCount
          }
        })
        .sort((a, b) => {
          if (a.status !== b.status) return a.status === 'live' ? -1 : 1
          return a.name.localeCompare(b.name)
        })

      return {
        definition,
        seatCount: linked.length,
        charts: chartUsages
      }
    })
  }

  /**
   * For legacy seats without roleDefinitionId, create a definition from
   * title/accountabilities and link it. Idempotent.
   */
  async migrateLegacyRoleDefinitions(): Promise<number> {
    try {
      const seats = await this.getAllRoles()
      const legacy = seats.filter(s => !s.roleDefinitionId && (s.title || '').trim())
      if (legacy.length === 0) return 0

      let created = 0
      for (const seat of legacy) {
        const defId = await this.createRoleDefinition({
          name: seat.title.trim(),
          accountabilities: seat.accountabilities || [],
          isContractor: !!seat.isContractor
        })
        await this.updateRole(seat.id, { roleDefinitionId: defId })
        created += 1
      }
      return created
    } catch (error) {
      console.error('Error migrating legacy role definitions:', error)
      throw error
    }
  }

  // ==========================================================================
  // ROLES CRUD (org-chart seats / cards)
  // ==========================================================================

  async getAllRoles(orgChartId?: string): Promise<Role[]> {
    try {
      const rolesRef = collection(db, 'roles')
      const snapshot = orgChartId
        ? await getDocs(query(rolesRef, where('orgChartId', '==', orgChartId)))
        : await getDocs(rolesRef)
      return snapshot.docs.map(docSnap => {
        const data = docSnap.data()
        // Handle legacy single-team data migration
        let teams = data.teams || []
        let teamColors = data.teamColors || []
        
        // If old format (single team string), convert to arrays
        if (data.team && !data.teams) {
          teams = [data.team]
          teamColors = data.teamColor ? [data.teamColor] : []
        }
        
        return {
          id: docSnap.id,
          ...data,
          orgChartId: data.orgChartId ?? '',
          roleDefinitionId: data.roleDefinitionId ?? null,
          order: typeof data.order === 'number' ? data.order : 0,
          isContractor: !!data.isContractor,
          teams,
          teamColors
        } as Role
      })
    } catch (error) {
      console.error('Error fetching roles:', error)
      throw error
    }
  }

  async getRole(roleId: string): Promise<Role | null> {
    try {
      const roleRef = doc(db, 'roles', roleId)
      const snapshot = await getDoc(roleRef)
      if (!snapshot.exists()) return null
      
      const data = snapshot.data()
      // Handle legacy single-team data migration
      let teams = data.teams || []
      let teamColors = data.teamColors || []
      
      if (data.team && !data.teams) {
        teams = [data.team]
        teamColors = data.teamColor ? [data.teamColor] : []
      }
      
      return {
        id: snapshot.id,
        ...data,
        orgChartId: data.orgChartId ?? '',
        roleDefinitionId: data.roleDefinitionId ?? null,
        order: typeof data.order === 'number' ? data.order : 0,
        isContractor: !!data.isContractor,
        teams,
        teamColors
      } as Role
    } catch (error) {
      console.error('Error fetching role:', error)
      throw error
    }
  }

  async createRole(roleData: Omit<Role, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const rolesRef = collection(db, 'roles')
      const newRoleRef = doc(rolesRef)
      await setDoc(newRoleRef, {
        ...roleData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      return newRoleRef.id
    } catch (error) {
      console.error('Error creating role:', error)
      throw error
    }
  }

  async updateRole(roleId: string, updates: Partial<Role>): Promise<void> {
    try {
      const roleRef = doc(db, 'roles', roleId)
      await updateDoc(roleRef, {
        ...updates,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error updating role:', error)
      throw error
    }
  }

  async deleteRole(roleId: string): Promise<void> {
    try {
      // First, update any roles that report to this one to have no parent
      const childRoles = await this.getRolesByParent(roleId)
      for (const childRole of childRoles) {
        await this.updateRole(childRole.id, { reportsToRoleId: null })
      }
      
      const roleRef = doc(db, 'roles', roleId)
      await deleteDoc(roleRef)
    } catch (error) {
      console.error('Error deleting role:', error)
      throw error
    }
  }

  async getRolesByParent(parentRoleId: string | null): Promise<Role[]> {
    try {
      const rolesRef = collection(db, 'roles')
      const q = parentRoleId 
        ? query(rolesRef, where('reportsToRoleId', '==', parentRoleId))
        : query(rolesRef, where('reportsToRoleId', '==', null))
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Role[]
    } catch (error) {
      console.error('Error fetching roles by parent:', error)
      throw error
    }
  }

  async assignMemberToRole(roleId: string, memberId: string | null): Promise<void> {
    try {
      await this.updateRole(roleId, { assignedMemberId: memberId })
    } catch (error) {
      console.error('Error assigning member to role:', error)
      throw error
    }
  }

  // ==========================================================================
  // TEAM MEMBERS CRUD
  // ==========================================================================

  async getAllTeamMembers(): Promise<TeamMember[]> {
    try {
      const membersRef = collection(db, 'teamMembers')
      const snapshot = await getDocs(membersRef)
      return snapshot.docs.map(docSnap => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          ...data,
          thumbnailPhotoUrl: normalizeThumbnailUrl(data.thumbnailPhotoUrl)
        } as TeamMember
      })
    } catch (error) {
      console.error('Error fetching team members:', error)
      throw error
    }
  }

  async getTeamMember(memberId: string): Promise<TeamMember | null> {
    try {
      const memberRef = doc(db, 'teamMembers', memberId)
      const snapshot = await getDoc(memberRef)
      if (!snapshot.exists()) return null
      const data = snapshot.data()
      return {
        id: snapshot.id,
        ...data,
        thumbnailPhotoUrl: normalizeThumbnailUrl(data.thumbnailPhotoUrl)
      } as TeamMember
    } catch (error) {
      console.error('Error fetching team member:', error)
      throw error
    }
  }

  async createTeamMember(memberData: Omit<TeamMember, 'lastSyncedAt'>): Promise<string> {
    try {
      const memberRef = doc(db, 'teamMembers', memberData.id)
      await setDoc(memberRef, {
        ...memberData,
        lastSyncedAt: serverTimestamp()
      })
      return memberData.id
    } catch (error) {
      console.error('Error creating team member:', error)
      throw error
    }
  }

  async updateTeamMember(memberId: string, updates: Partial<TeamMember>): Promise<void> {
    try {
      const memberRef = doc(db, 'teamMembers', memberId)
      await updateDoc(memberRef, {
        ...updates,
        lastSyncedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error updating team member:', error)
      throw error
    }
  }

  async deleteTeamMember(memberId: string): Promise<void> {
    try {
      // First, unassign from any roles
      const roles = await this.getAllRoles()
      for (const role of roles) {
        if (role.assignedMemberId === memberId) {
          await this.updateRole(role.id, { assignedMemberId: null })
        }
      }
      
      const memberRef = doc(db, 'teamMembers', memberId)
      await deleteDoc(memberRef)
    } catch (error) {
      console.error('Error deleting team member:', error)
      throw error
    }
  }

  async searchTeamMembers(searchTerm: string): Promise<TeamMember[]> {
    try {
      const allMembers = await this.getAllTeamMembers()
      const lowerSearch = searchTerm.toLowerCase()
      return allMembers.filter(member => 
        member.name.fullName.toLowerCase().includes(lowerSearch) ||
        member.primaryEmail.toLowerCase().includes(lowerSearch) ||
        (member.jobTitle && member.jobTitle.toLowerCase().includes(lowerSearch))
      )
    } catch (error) {
      console.error('Error searching team members:', error)
      throw error
    }
  }

  // ==========================================================================
  // TEAMS CRUD
  // ==========================================================================

  async getAllTeams(): Promise<Team[]> {
    try {
      const teamsRef = collection(db, 'teams')
      const q = query(teamsRef, orderBy('name'))
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Team[]
    } catch (error) {
      console.error('Error fetching teams:', error)
      throw error
    }
  }

  async getTeam(teamId: string): Promise<Team | null> {
    try {
      const teamRef = doc(db, 'teams', teamId)
      const snapshot = await getDoc(teamRef)
      if (!snapshot.exists()) return null
      return { id: snapshot.id, ...snapshot.data() } as Team
    } catch (error) {
      console.error('Error fetching team:', error)
      throw error
    }
  }

  async createTeam(teamData: Omit<Team, 'id' | 'createdAt'>): Promise<string> {
    try {
      const teamsRef = collection(db, 'teams')
      const newTeamRef = doc(teamsRef)
      await setDoc(newTeamRef, {
        ...teamData,
        createdAt: serverTimestamp()
      })
      return newTeamRef.id
    } catch (error) {
      console.error('Error creating team:', error)
      throw error
    }
  }

  async updateTeam(teamId: string, updates: Partial<Team>): Promise<void> {
    try {
      const teamRef = doc(db, 'teams', teamId)
      await updateDoc(teamRef, updates)
    } catch (error) {
      console.error('Error updating team:', error)
      throw error
    }
  }

  async deleteTeam(teamId: string): Promise<void> {
    try {
      // Get the team to find its name
      const team = await this.getTeam(teamId)
      if (!team) return

      // Remove team assignment from any roles that have this team
      const roles = await this.getAllRoles()
      for (const role of roles) {
        const teams = role.teams || []
        const teamIndex = teams.indexOf(team.name)
        if (teamIndex !== -1) {
          const newTeams = teams.filter(t => t !== team.name)
          const newColors = (role.teamColors || []).filter((_, i) => i !== teamIndex)
          await this.updateRole(role.id, { teams: newTeams, teamColors: newColors })
        }
      }
      
      const teamRef = doc(db, 'teams', teamId)
      await deleteDoc(teamRef)
    } catch (error) {
      console.error('Error deleting team:', error)
      throw error
    }
  }

  // ==========================================================================
  // COMPOSITE OPERATIONS
  // ==========================================================================

  /**
   * Count of team directory members not marked hidden (for metrics such as revenue per person).
   */
  async getNonHiddenTeamMemberCount(): Promise<number> {
    const members = await this.getAllTeamMembers()
    return members.filter((m) => !m.hidden).length
  }

  /**
   * Get all roles with their assigned members and computed properties
   */
  async getRolesWithMembers(orgChartId?: string): Promise<RoleWithMember[]> {
    try {
      const chartId = orgChartId ?? (await this.getLiveOrgChart())?.id
      const [roles, members, definitions] = await Promise.all([
        chartId ? this.getAllRoles(chartId) : this.getAllRoles(),
        this.getAllTeamMembers(),
        this.getAllRoleDefinitions()
      ])

      const membersMap = new Map(members.map(m => [m.id, m]))
      const definitionsMap = new Map(definitions.map(d => [d.id, d]))
      
      // Build a map of role children for counting direct reports
      const childrenCount = new Map<string, number>()
      for (const role of roles) {
        if (role.reportsToRoleId) {
          childrenCount.set(
            role.reportsToRoleId, 
            (childrenCount.get(role.reportsToRoleId) || 0) + 1
          )
        }
      }

      // Build role map for team inheritance lookup
      const rolesMap = new Map(roles.map(r => [r.id, r]))

      // Function to get inherited teams from DIRECT parent only (one level)
      const getInheritedTeams = (role: Role): { teams: string[], colors: string[] } => {
        // Only inherit from direct parent, not grandparents
        if (!role.reportsToRoleId) {
          return { teams: [], colors: [] }
        }
        
        const parent = rolesMap.get(role.reportsToRoleId)
        if (!parent) {
          return { teams: [], colors: [] }
        }
        
        // Only inherit if parent has direct teams assigned
        if (parent.teams && parent.teams.length > 0) {
          return { teams: [...parent.teams], colors: [...(parent.teamColors || [])] }
        }
        
        return { teams: [], colors: [] }
      }

      return roles.map(role => {
        // Only get inherited teams if this role has no direct teams
        const inherited = (role.teams && role.teams.length > 0) 
          ? { teams: [], colors: [] } 
          : getInheritedTeams(role)

        const definition = role.roleDefinitionId
          ? definitionsMap.get(role.roleDefinitionId)
          : undefined
        
        return {
          ...role,
          // Prefer live definition so edits show immediately across cards
          title: definition?.name ?? role.title,
          accountabilities: definition?.accountabilities ?? role.accountabilities ?? [],
          isContractor: definition
            ? !!definition.isContractor
            : !!role.isContractor,
          roleDefinitionId: role.roleDefinitionId ?? null,
          // Ensure teams/teamColors are always arrays (handle legacy single-team data)
          teams: role.teams || [],
          teamColors: role.teamColors || [],
          assignedMember: role.assignedMemberId 
            ? membersMap.get(role.assignedMemberId) || null 
            : null,
          directReportsCount: childrenCount.get(role.id) || 0,
          inheritedTeams: inherited.teams,
          inheritedTeamColors: inherited.colors
        }
      })
    } catch (error) {
      console.error('Error fetching roles with members:', error)
      throw error
    }
  }

  /**
   * Get members grouped by their team assignments (through roles)
   */
  async getMembersByTeam(): Promise<Map<string, { team: Team, members: Array<{ member: TeamMember, role: Role }> }>> {
    try {
      const [teams, rolesWithMembers] = await Promise.all([
        this.getAllTeams(),
        this.getRolesWithMembers()
      ])

      const result = new Map<string, { team: Team, members: Array<{ member: TeamMember, role: Role }> }>()

      // Initialize with all teams
      for (const team of teams) {
        result.set(team.name, { team, members: [] })
      }

      // Group members by their effective teams (direct or inherited)
      for (const role of rolesWithMembers) {
        if (!role.assignedMember) continue
        
        // Get effective teams (direct teams or inherited if none)
        const effectiveTeams = (role.teams && role.teams.length > 0) 
          ? role.teams 
          : role.inheritedTeams
        
        if (!effectiveTeams || effectiveTeams.length === 0) continue

        // Add member to each team they belong to
        for (const teamName of effectiveTeams) {
          const teamData = result.get(teamName)
          if (teamData) {
            teamData.members.push({ member: role.assignedMember, role })
          }
        }
      }

      return result
    } catch (error) {
      console.error('Error getting members by team:', error)
      throw error
    }
  }

  /**
   * Get the org chart hierarchy starting from root roles
   */
  async getOrgChartHierarchy(): Promise<RoleWithMember[]> {
    try {
      const rolesWithMembers = await this.getRolesWithMembers()
      
      // Sort so that roles without parents come first, then by order (desc), then title
      return rolesWithMembers.sort((a, b) => {
        // Root roles (no parent) should be last for bottom-up display
        if (!a.reportsToRoleId && b.reportsToRoleId) return 1
        if (a.reportsToRoleId && !b.reportsToRoleId) return -1
        const orderDiff = (b.order ?? 0) - (a.order ?? 0)
        if (orderDiff !== 0) return orderDiff
        return a.title.localeCompare(b.title)
      })
    } catch (error) {
      console.error('Error getting org chart hierarchy:', error)
      throw error
    }
  }

  /**
   * Get all roles assigned to a specific member
   */
  async getRolesForMember(memberId: string): Promise<Role[]> {
    try {
      const rolesRef = collection(db, 'roles')
      const q = query(rolesRef, where('assignedMemberId', '==', memberId))
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Role[]
    } catch (error) {
      console.error('Error fetching roles for member:', error)
      throw error
    }
  }

  /**
   * Get members with location data for the map view
   */
  async getMembersWithLocations(): Promise<TeamMember[]> {
    try {
      const members = await this.getAllTeamMembers()
      return members.filter(m => m.location && m.location.label && !m.hidden)
    } catch (error) {
      console.error('Error fetching members with locations:', error)
      throw error
    }
  }

  /**
   * Get unassigned members (not assigned to any role)
   */
  async getUnassignedMembers(): Promise<TeamMember[]> {
    try {
      const [members, roles] = await Promise.all([
        this.getAllTeamMembers(),
        this.getAllRoles()
      ])

      const assignedMemberIds = new Set(
        roles
          .filter(r => r.assignedMemberId)
          .map(r => r.assignedMemberId!)
      )

      return members.filter(m => !assignedMemberIds.has(m.id))
    } catch (error) {
      console.error('Error fetching unassigned members:', error)
      throw error
    }
  }
}
